import { Injectable } from 'angular-ts-decorators';

import { cleanDiacritics } from 'underscore.string';
import * as CodepageEncoder from 'codepage-encoder';

import { Domain } from '@shared/app/scripts/services/domain';
import type { Navigator } from '@shared/app/scripts/services/navigator';
import { SettingsManager } from '@shared/app/scripts/services/settings-manager';

export interface IPrinterSettings {
  printerId: string;
  printerType: string;
}

const ESC = String.fromCharCode(27);

/**
 * Service that provides a direct access & default interface for all available
 * printers. The service is meant to be used across v7; as such it's expected to
 * be extracted to v6 friendly module.
 */
@Injectable('printer')
export class Printer {
  /**
   * Prints using HTML template with Epson thermal printers. Only compatible
   * with printers which use the Epson APD v4 and higher.
   * @param body
   * Text to be provided to the printer mechanism.
   * @param cutter
   * True, if the cutter command should be added at the end of the printout
   * @param drawerKick
   * True, if the drawerKick command should be added at the beginning of the printout
   * @return
   * Resolve to true on successfully print attempt, reject otherwise.
   */
  public localThermal(
    body: string,
    cutter: boolean = false,
    drawerKick: boolean = false,
  ): ng.IPromise<any> {
    const deferred = this.$q.defer();

    if (angular.isFunction(this.browserCallback)) {
      this.browserCallback(
        `<html>
           <head>
             <style>
               body {font-family: "Lucida Console";}
               .receipt {font-family: "Lucida Console";}
               .barcode {font-family: "Consolas";}
               .control {font-family: "Courier New";}
             </style>
           </head>
           <body>${drawerKick ? '<p class="control">P</p>' : ''}
             <pre class="receipt">${body}</pre>
           ${cutter ? '<p class="control">A</p>' : ''}</body>
         </html>`,
      );

      // make sure that page is updated before print
      this.$timeout(() => {
        this.$window.print();
        this.browserCallback('');
        deferred.resolve('Print successfully');
      });
    } else {
      deferred.reject(
        'Cannot use browser printer, please use sw-app directive in your index.html',
      );
    }

    return deferred.promise;
  }

  private browserCallback: (...args: [string]) => void;

  constructor(
    private $http: ng.IHttpService,
    private $log: ng.ILogService,
    private $q: ng.IQService,
    private $rootScope: ng.IRootScopeService,
    private $timeout: ng.ITimeoutService,
    private $window: ng.IWindowService,
    private domain: Domain,
    private navigator: Navigator,
    private settingsManager: SettingsManager,
  ) {
    'ngInject';
  }

  /**
   * Prints using printer queue v2
   * @param body
   * Text to be provided to the printer mechanism. Clear text or html
   * depending on capabilities of a printer.
   * @param cutter
   * True, if the cutter command should be added at the end of the printout
   * @param drawerKick
   * True, if the drawerKick command should be added at the beginning of the printout
   * @return
   * Resolve to true on successfully print attempt, reject otherwise.
   */
  printerQ2(
    body: string,
    cutter: boolean = false,
    drawerKick: boolean = false,
  ): ng.IPromise<any> {
    const baseUrl = '//' + this.domain.createSibling('pq2');

    let payload = '';

    if (drawerKick) {
      payload += ESC + 'p0  ';
    }

    payload += cleanDiacritics(body);

    if (cutter) {
      payload += ESC + 'm';
    }

    return this.$http.post(
      baseUrl + '/queue?queue=' + this.settingsManager.settings.printerId,
      payload,
    );
  }

  /**
   * Prints PDF using printer queue v2
   * @param  body
   * Content of the file to be printed.
   * @return
   * Promise with printing result.
   */
  printPdf(body: string): ng.IPromise<any> {
    const baseUrl = '//' + this.domain.createSibling('pq2');

    const printerId = this.settingsManager.settings.printerId;

    return this.$http
      .post(baseUrl + '/queue?queue=' + printerId, body, {
        headers: { 'Content-Type': 'application/pdf' },
      })
      .then(() => 'Data provided to print queue v2 ' + printerId);
  }

  /**
   * Prints using printer queue v2
   * @param body
   * Text to be provided to the printer mechanism.
   * @param printerId
   * Key that will be used in communication with printerQueue
   * @return
   * Promise with printing result.
   */
  rawPQ2(body: string, printerId: string): ng.IPromise<any> {
    const baseUrl = '//' + this.domain.createSibling('pq2');

    return this.$http
      .post(baseUrl + '/queue?queue=' + printerId, body)
      .then(() => 'Data provided to print queue v2 ' + printerId);
  }

  /**
   * Prints using default browser settings.
   * @param body
   * Text to be provided to the printer mechanism.
   * @param cutter
   * Irrevelant for browser printing
   * @param drawerKick
   * Irrevelant for browser printing
   * @return
   * Resolve to true on successfully print attempt, reject otherwise.
   */
  browser(
    body: string,
    _cutter: boolean = false, // eslint-disable-line @typescript-eslint/no-unused-vars
    _drawerKick: boolean = false, // eslint-disable-line @typescript-eslint/no-unused-vars
  ): ng.IPromise<any> {
    const deferred = this.$q.defer();

    if (angular.isFunction(this.browserCallback)) {
      this.browserCallback(body);
      // make sure that page is updated before print
      this.$timeout(() => {
        this.$window.print();
        this.browserCallback('');
        deferred.resolve('Print successfully');
      });
    } else {
      deferred.reject(
        'Cannot use browser printer, please use sw-app directive in your index.html',
      );
    }

    return deferred.promise;
  }

  /**
   * Prints using usb
   * @param body
   * Text to be provided to the printer mechanism.
   * @param cutter
   * True, if the cutter command should be added at the end of the printout
   * @param drawerKick
   * True, if the drawerKick command should be added at the beginning of the printout
   * @return
   * Resolve to true on successfully print attempt, reject otherwise.
   */
  usb(
    body: string,
    cutter: boolean = false,
    drawerKick: boolean = false,
  ): ng.IPromise<any> {
    const deferred = this.$q.defer();

    this.$log.log('printer.usb: starting');

    let usbPrinterDevice, usbPrinterDevicePromise;

    this.$log.log('printer.usb: device not connected');
    if (!this.navigator.usb) {
      this.$log.log('printer.usb: API not available');
      usbPrinterDevicePromise = this.$q.reject(
        'Usb API is not available. Check if you are in a secure context & using chrome, edge or opera browser.',
      );
    } else {
      this.$log.log('printer.usb: finding device');
      usbPrinterDevicePromise = this.navigator.usb
        .getDevices()
        .then((devices: any) => {
          if (devices.length > 0) {
            this.$log.log('printer.usb: found device connected previously');
            return devices[0];
          } else {
            this.$log.log('printer.usb: searching new devices');
            return this.navigator.usb.requestDevice({
              filters: [{ vendorId: 0x04b8 }, { vendorId: 0x1fc9 }],
            });
          }
        })
        .then((device: any) => {
          usbPrinterDevice = device;

          this.$log.log('printer.usb: connecting to a device');

          return usbPrinterDevice.open();
        })
        .then(() => usbPrinterDevice.selectConfiguration(1))
        .then(() => usbPrinterDevice.claimInterface(0));
    }

    let payload = '';

    if (drawerKick) {
      payload += ESC + 'p0  ';
    }

    payload += cleanDiacritics(body);

    if (cutter) {
      payload += ESC + 'm';
    }

    const data = CodepageEncoder.encode('iso885915', payload);

    this.$log.log('printer.usb: printing on the connected device');

    usbPrinterDevicePromise
      .then(() => {
        this.$log.log('printer.usb: sending out the data');
        return usbPrinterDevice.transferOut(1, data);
      })
      .then(
        () => {
          this.$log.log('printer.usb: printing finished');

          deferred.resolve('Print successfully');

          // force into angular the native promise
          this.$rootScope.$digest();
        },
        (error: any) => {
          this.$log.log('printer.usb: printing failed');

          deferred.reject(error);
        },
      );

    return deferred.promise;
  }

  /**
   * Dummy printing method, does nothing but succed immedielty
   * @param body
   * dummy parameter
   * @param printerId
   * dummy parameter
   * @return
   * Promise that always resolves
   */
  none(body: string, printerId: string): ng.IPromise<any> {
    return this.$q.when();
  }

  /**
   * Prints using mechanism provided in setDefault call. Direct call to one
   * of:
   * * browser
   * * localThermal
   * * printerQ2
   * * usb
   * methods.
   * @param body
   * Text to be provided to the printer mechanism.
   * @param cutter
   * True, if the cutter command should be added at the end of the printout
   * @param drawerKick
   * True, if the drawerKick command should be added at the beginning of the printout
   * @return
   * Resolve to true on successfully print attempt, reject otherwise.
   */
  useDefault(
    body: string,
    cutter: boolean = false,
    drawerKick: boolean = false,
  ): ng.IPromise<any> {
    return this[this.settingsManager.settings.printer](
      body,
      // @ts-ignore
      cutter,
      drawerKick,
    );
  }

  /**
   * Saves a callback to set the print output to the screen.
   * @param {function} callback
   * Function that when called with a printout body updates the screen content.
   * @return {void}
   * Nothing
   */
  setBrowserCallback(callback: (...args: [string]) => void): void {
    this.browserCallback = callback;
  }
}
